import { format } from 'date-fns'
import { MdMoreVert } from 'react-icons/md'
import { Cell } from 'react-table'
import {
  Badge,
  Button,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@ritualco/jutsu'
import { useContent } from 'lib/content/react'
import { useHistory } from 'lib/router'
import {
  IncentivePlan,
  IncentivePlanStatus,
  IncentiveType,
  ScheduleType,
} from 'types/entities'

export const Title = ({ value }: { value?: string }) => {
  const { formatMessage } = useContent()
  if (!value)
    return formatMessage({
      id: 'pmportal.incentives.untitledIncentive',
    })
  return value
}

export const Type = ({ value }: { value?: IncentiveType }) => {
  const { formatMessage } = useContent()
  if (!value) return '-'
  switch (value) {
    case 'creditOneTime':
    case 'creditRecurring':
      return formatMessage({ id: 'pmportal.incentives.creditType' })
    case 'discountAmount':
    case 'discountPercentage':
      return formatMessage({ id: 'pmportal.incentives.discountType' })
  }
}

export const Available = ({
  value,
  row: { original },
}: Cell<IncentivePlan, ScheduleType | undefined>) => {
  const { formatMessage } = useContent()
  if (!value) return '-'
  switch (value) {
    case 'none':
      const startDate = original.incentiveDetails.schedule?.start
      if (!startDate) return '-'
      return format(startDate, 'LLLL d')
    case 'daily':
      return formatMessage({ id: 'pmportal.incentives.daily' })
    case 'weekly':
      return formatMessage({ id: 'pmportal.incentives.weekly' })
    case 'monthly':
      return formatMessage({ id: 'pmportal.incentives.monthly' })
    case 'yearly':
      return formatMessage({ id: 'pmportal.incentives.yearly' })
    case 'custom':
      return formatMessage({ id: 'pmportal.incentives.custom' })
  }
}

export const Status = ({ value }: { value: IncentivePlanStatus }) => {
  const { formatMessage } = useContent()
  switch (value) {
    case 'draft':
      return (
        <Badge colorScheme="gray">
          {formatMessage({ id: 'pmportal.incentives.draftStatus' })}
        </Badge>
      )
    case 'upcoming':
      return (
        <Badge>
          {formatMessage({ id: 'pmportal.incentives.upcomingStatus' })}
        </Badge>
      )
    case 'live':
      return (
        <Badge>{formatMessage({ id: 'pmportal.incentives.liveStatus' })}</Badge>
      )
    case 'paused':
      return (
        <Badge colorScheme="yellow">
          {formatMessage({ id: 'pmportal.incentives.pausedStatus' })}
        </Badge>
      )
    case 'ended':
      return (
        <Badge colorScheme="gray">
          {formatMessage({ id: 'pmportal.incentives.endedStatus' })}
        </Badge>
      )
  }
}

export type ActionCallbacks = {
  onLaunch: (incentivePlanId: string) => void
  onRelaunch: (incentivePlanId: string) => void
  onPause: (incentivePlanId: string) => void
  onResume: (incentivePlanId: string) => void
  onDelete: (incentivePlanId: string) => void
}

type ActionsProps = {
  incentivePlan: IncentivePlan
} & ActionCallbacks
export const Actions = ({
  incentivePlan,
  onLaunch,
  onRelaunch,
  onPause,
  onResume,
  onDelete,
}: ActionsProps) => {
  const history = useHistory()
  const { formatMessage } = useContent()

  return (
    <HStack spacing={2} justifyContent="flex-end">
      <Button
        colorScheme="transparent"
        variant="outline"
        size="sm"
        onClick={() => {
          history.push(`incentives/${incentivePlan.id}`)
        }}
      >
        {formatMessage({ id: 'pmportal.global.actionView' })}
      </Button>
      <Menu>
        <MenuButton
          as={IconButton}
          colorScheme="transparent"
          variant="ghost"
          size="sm"
          icon={<Icon as={MdMoreVert} />}
        />
        <MenuList zIndex="dropdown">
          {incentivePlan.canLaunch && (
            <MenuItem
              onClick={() => {
                onLaunch(incentivePlan.id)
              }}
            >
              {formatMessage({ id: 'pmportal.incentives.launchAction' })}
            </MenuItem>
          )}
          {incentivePlan.canRelaunch && (
            <MenuItem
              onClick={() => {
                onRelaunch(incentivePlan.id)
              }}
            >
              {formatMessage({
                id: 'pmportal.incentives.relaunchAction',
              })}
            </MenuItem>
          )}
          {incentivePlan.canPause && (
            <MenuItem
              onClick={() => {
                onPause(incentivePlan.id)
              }}
            >
              {formatMessage({ id: 'pmportal.incentives.pauseAction' })}
            </MenuItem>
          )}
          {incentivePlan.canResume && (
            <MenuItem
              onClick={() => {
                onResume(incentivePlan.id)
              }}
            >
              {formatMessage({ id: 'pmportal.incentives.resumeAction' })}
            </MenuItem>
          )}
          {incentivePlan.canDelete && (
            <MenuItem
              onClick={() => {
                onDelete(incentivePlan.id)
              }}
            >
              {formatMessage({ id: 'pmportal.incentives.deleteAction' })}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              // TODO(WT-1888): handle copying
            }}
          >
            {formatMessage({ id: 'pmportal.incentives.copyAction' })}
          </MenuItem>
        </MenuList>
      </Menu>
    </HStack>
  )
}
